import React, { useState } from 'react';

import ArrowLeftIcon from '@mui/icons-material/ArrowLeft';
import ArrowRightIcon from '@mui/icons-material/ArrowRight';
import BusinessIcon from '@mui/icons-material/Business';
import CloseIcon from '@mui/icons-material/Close';
import FolderIcon from '@mui/icons-material/Folder';
import SettingsIcon from '@mui/icons-material/Settings';
import { styled } from '@mui/material/styles';
import {
    Box,
    Divider,
    IconButton,
    List,
    ListItem,
    ListItemText,
    Typography,
} from '@sunwisesoftware/sunwise-ui';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';

import CheckAccess from 'common/components/CheckAccess';
import { useBreakpoint } from 'common/hooks';

import IcoPlans from 'resources/icoPlans.svg?react';
import IcoProducts from 'resources/icoProducts.svg?react';
import IcoTemplates from 'resources/icoTemplates.svg?react';

import Logo from './Logo';
import ProfileButton from './ProfileButton';
import ProfileMenu from './ProfileMenu';
import ProfileMenuItems from './ProfileMenuItems';
import SecondaryDrawer from './SecondaryDrawer';
import StyledListItemButton from './StyledListItemButton';
import TooltipHandler from './TooltipHandler';

const Name = styled(Typography)(() => ({
    color: '#EEF4FA',
    display: 'block',
    fontSize: '14px',
    fontWeight: 400,
    letterSpacing: '0.25px',
    lineHeight: '20px',
    textAlign: 'left',
}));

const DrawerContent = ({
    container,
    drawerSize,
    handleDrawerToggle,
    match,
    mobileOpen,
    profileValues,
    redirect,
    selectedTheme,
    setTheme,
    toggleDrawerSize,
}) => {
    const { t } = useTranslation();
    const breakpoint = useBreakpoint();
    const [anchorProfileMenu, setAnchorProfileMenu] = useState(null);
    const handleProfileMenuOpen = (event) => {
        if (mobileOpen && ['xs', 'sm'].includes(breakpoint)) {
            handleProfileDrawerToggle();
            return;
        }
        setAnchorProfileMenu(event.currentTarget);
    };
    const [mobileOpenProfileDrawer, setMobileOpenProfileDrawer] =
        useState(false);
    const handleProfileDrawerToggle = () =>
        setMobileOpenProfileDrawer(!mobileOpenProfileDrawer);

    return (
        <>
            <Box
                display="flex"
                sx={{
                    alignItems: 'center',
                    justifyContent: { md: 'center', xs: 'space-between' },
                    px: { md: 0, xs: 2 },
                }}
            >
                <Box sx={{ padding: '8px 4px' }}>
                    <Logo />
                </Box>

                <IconButton
                    onClick={() => {
                        if (mobileOpen) handleDrawerToggle();
                    }}
                    sx={{ display: { md: 'none' } }}
                >
                    <CloseIcon />
                </IconButton>
            </Box>

            <Divider sx={{ display: { md: 'none', sx: 'block' } }} />

            <Box
                component="span"
                className="toggleDrawer"
                sx={{
                    color: '#4a4a5d',
                    cursor: 'pointer',
                    display: { md: 'block', xs: 'none' },
                    textAlign: 'right',
                    visibility: 'hidden',
                }}
                onClick={toggleDrawerSize}
            >
                {drawerSize === 'normal' ? (
                    <ArrowLeftIcon />
                ) : (
                    <ArrowRightIcon />
                )}
            </Box>

            <List
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    height: '100%',
                    pb: 0,
                }}
            >
                <CheckAccess requiredPermissions={[1]}>
                    <ListItem disablePadding>
                        <TooltipHandler
                            drawerSize={drawerSize}
                            title={t('Company', { count: 2 })}
                        >
                            <StyledListItemButton
                                onClick={() => {
                                    redirect('/companies');
                                    if (mobileOpen) handleDrawerToggle();
                                }}
                                selected={
                                    match.path === '/companies' ||
                                    /^\/companies/i.test(match.path) ||
                                    /^\/company-file/i.test(match.path)
                                }
                                size={drawerSize}
                            >
                                <BusinessIcon />
                                <ListItemText
                                    primary={t('Company', { count: 2 })}
                                />
                            </StyledListItemButton>
                        </TooltipHandler>
                    </ListItem>
                </CheckAccess>

                <CheckAccess requiredPermissions={[2]}>
                    <ListItem disablePadding>
                        <TooltipHandler
                            drawerSize={drawerSize}
                            title={t('Proposal', { count: 2 })}
                        >
                            <StyledListItemButton
                                onClick={() => {
                                    handleDrawerToggle();
                                    redirect('/proposals');
                                    if (mobileOpen) handleDrawerToggle();
                                }}
                                selected={/^\/proposals/i.test(match.path)}
                                size={drawerSize}
                            >
                                <FolderIcon />
                                <ListItemText
                                    primary={t('Proposal', { count: 2 })}
                                />
                            </StyledListItemButton>
                        </TooltipHandler>
                    </ListItem>
                </CheckAccess>

                <CheckAccess requiredPermissions={[2]}>
                    <ListItem disablePadding>
                        <TooltipHandler
                            drawerSize={drawerSize}
                            title={t('Product', { count: 2 })}
                        >
                            <StyledListItemButton
                                onClick={() => {
                                    handleDrawerToggle();
                                    redirect('/products');
                                    if (mobileOpen) handleDrawerToggle();
                                }}
                                selected={match.path === '/products'}
                                size={drawerSize}
                            >
                                <IcoProducts />
                                <ListItemText
                                    primary={t('Product', { count: 2 })}
                                />
                            </StyledListItemButton>
                        </TooltipHandler>
                    </ListItem>
                </CheckAccess>

                <CheckAccess requiredPermissions={[2]}>
                    <ListItem disablePadding>
                        <TooltipHandler
                            drawerSize={drawerSize}
                            title={t('System', { count: 2 })}
                        >
                            <StyledListItemButton
                                onClick={() => {
                                    redirect('/systems');
                                    if (mobileOpen) handleDrawerToggle();
                                }}
                                selected={/^\/system/i.test(match.path)}
                                size={drawerSize}
                            >
                                <IcoPlans />
                                <ListItemText
                                    primary={t('System', { count: 2 })}
                                />
                            </StyledListItemButton>
                        </TooltipHandler>
                    </ListItem>
                </CheckAccess>

                <CheckAccess requiredPermissions={[3]}>
                    <ListItem disablePadding>
                        <TooltipHandler
                            drawerSize={drawerSize}
                            title={t('Document', { count: 2 })}
                        >
                            <StyledListItemButton
                                onClick={() => {
                                    redirect('/documents');
                                    if (mobileOpen) handleDrawerToggle();
                                }}
                                selected={/^\/documents/i.test(match.path)}
                                size={drawerSize}
                            >
                                <IcoTemplates />
                                <ListItemText
                                    primary={t('Document', { count: 2 })}
                                />
                            </StyledListItemButton>
                        </TooltipHandler>
                    </ListItem>
                </CheckAccess>

                <ListItem disablePadding sx={{ marginTop: 'auto' }}>
                    <Box sx={{ width: '100%' }}>
                        <Divider
                            sx={{
                                display: { md: 'none', sx: 'block' },
                                marginTop: 'auto',
                            }}
                        />

                        <StyledListItemButton
                            selected={match.path === '/profile'}
                            size={drawerSize}
                            sx={{
                                alignItems: 'center',
                                display: 'flex',
                                justifyContent: 'center',
                                p: '8px 4px',
                            }}
                            onClick={handleProfileMenuOpen}
                        >
                            <ProfileButton profileValues={profileValues} />

                            <Name sx={{ display: { md: 'none', xs: 'block' } }}>
                                {profileValues?.first_name}{' '}
                                {profileValues?.last_name}
                            </Name>

                            <IconButton
                                sx={{
                                    display: { md: 'none', xs: 'flex' },
                                    ml: 'auto',
                                }}
                            >
                                <SettingsIcon />
                            </IconButton>
                        </StyledListItemButton>
                    </Box>
                </ListItem>
            </List>

            <SecondaryDrawer
                {...{
                    container,
                    match,
                    mobileOpen: mobileOpenProfileDrawer,
                    onClose: handleProfileDrawerToggle,
                    redirect,
                }}
            >
                <ProfileMenuItems
                    {...{
                        handleCloseProfileMenu: () => {
                            handleProfileDrawerToggle();
                            handleDrawerToggle();
                        },
                        match,
                        profileValues,
                        redirect,
                        selectedTheme,
                        setTheme,
                    }}
                />
            </SecondaryDrawer>

            <ProfileMenu
                anchorProfileMenu={anchorProfileMenu}
                handleDrawerToggle={handleDrawerToggle}
                match={match}
                mobileOpen={mobileOpen}
                profileValues={profileValues}
                redirect={redirect}
                selectedTheme={selectedTheme}
                setAnchorProfileMenu={setAnchorProfileMenu}
                setTheme={setTheme}
            />
        </>
    );
};

DrawerContent.propTypes = {
    container: PropTypes.func,
    drawerSize: PropTypes.string,
    handleDrawerToggle: PropTypes.func,
    match: PropTypes.object,
    mobileOpen: PropTypes.bool,
    profileValues: PropTypes.object,
    redirect: PropTypes.func,
    selectedTheme: PropTypes.string,
    setTheme: PropTypes.func,
    toggleDrawerSize: PropTypes.func,
};

export default DrawerContent;
